import { registerEdgeType } from '../services/EdgeTypeRegistry';
import { straightEdgeType } from './straightEdgeType';
import type { EdgeTypeDefinition, EdgeBoundsContext, EdgeLineBounds, TextAnchor } from '../types/EdgeTypes';

const CURVE_OFFSET_RATIO = 0.2;

function createPath(
  ctx: CanvasRenderingContext2D,
  startX: number,
  startY: number,
  endX: number,
  endY: number
): void {
  // Template preview fallback: draw horizontal line
  straightEdgeType.createPath(ctx, startX, startY, endX, endY);
}

function _getControlPoint(fromX: number, fromY: number, toX: number, toY: number): { cpX: number; cpY: number } {
  const midX = (fromX + toX) / 2;
  const midY = (fromY + toY) / 2;
  const dx = toX - fromX;
  const dy = toY - fromY;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  // Perpendicular unit vector
  const perpX = -dy / len;
  const perpY = dx / len;
  const offset = len * CURVE_OFFSET_RATIO;
  return {
    cpX: midX + perpX * offset,
    cpY: midY + perpY * offset,
  };
}

function createWorldPath(
  ctx: CanvasRenderingContext2D,
  fromX: number,
  fromY: number,
  toX: number,
  toY: number
): Path2D {
  const { cpX, cpY } = _getControlPoint(fromX, fromY, toX, toY);

  const path = new Path2D();
  path.moveTo(fromX, fromY);
  path.quadraticCurveTo(cpX, cpY, toX, toY);

  ctx.beginPath();
  ctx.moveTo(fromX, fromY);
  ctx.quadraticCurveTo(cpX, cpY, toX, toY);

  return path;
}

function getTangentAtStart(fromX: number, fromY: number, toX: number, toY: number): number {
  // Tangent at t=0 of quadratic bezier points from start toward control point
  const { cpX, cpY } = _getControlPoint(fromX, fromY, toX, toY);
  return Math.atan2(cpY - fromY, cpX - fromX);
}

function getTangentAtEnd(fromX: number, fromY: number, toX: number, toY: number): number {
  // Tangent at t=1 of quadratic bezier points from control point toward end
  const { cpX, cpY } = _getControlPoint(fromX, fromY, toX, toY);
  return Math.atan2(toY - cpY, toX - cpX);
}

function getTextAnchor(fromX: number, fromY: number, toX: number, toY: number): TextAnchor {
  // Evaluate quadratic bezier at t=0.5
  const { cpX, cpY } = _getControlPoint(fromX, fromY, toX, toY);
  const t = 0.5;
  const mt = 1 - t;
  const x = mt * mt * fromX + 2 * mt * t * cpX + t * t * toX;
  const y = mt * mt * fromY + 2 * mt * t * cpY + t * t * toY;

  // Tangent at t=0.5: derivative of quadratic bezier
  const tx = 2 * mt * (cpX - fromX) + 2 * t * (toX - cpX);
  const ty = 2 * mt * (cpY - fromY) + 2 * t * (toY - cpY);
  const angle = Math.atan2(ty, tx);

  return { x, y, angle };
}

function calculateLineBounds(context: EdgeBoundsContext): EdgeLineBounds {
  return straightEdgeType.calculateLineBounds(context);
}

const curvedEdgeType: EdgeTypeDefinition = {
  value: 'curved',
  worldSpace: true,
  createPath,
  calculateLineBounds,
  createWorldPath,
  getTangentAtStart,
  getTangentAtEnd,
  getTextAnchor,
};

registerEdgeType(curvedEdgeType);

export { curvedEdgeType };
